import { useState, useEffect } from 'react'
import './UpsellPopUp.css'
import { translate } from '../../../../utils/translator'

const SMALL_PHONE_WIDTH = 380;

const BulletDot = () => (
  <span className="mr-1.5 text-[7px] leading-[18px]">•</span>
)

const CheckIconGreen = () => (
  <svg className="shrink-0 w-4 h-4" fill="none" stroke="#15967C" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
  </svg>
)

interface ServiceSectionMobileBProps {
  premiumPrice: number;
  regularPrice: number;
  savingsAmount?: number;
  isPremiumCheaper?: boolean;
  language?: string;
}

export default function ServiceSectionMobileB({ premiumPrice, regularPrice, savingsAmount = 0, isPremiumCheaper = false, language }: ServiceSectionMobileBProps) {
  const [isSmallPhone, setIsSmallPhone] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsSmallPhone(window.innerWidth < SMALL_PHONE_WIDTH);
    };
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const t = (key: string) => translate(language, "shipping", `UpsellPopUpV2/${key}`) as string;
  const sp = isSmallPhone;

  return (
    <div id="upgrade-order-modal-service-cards" className={`flex flex-col ${sp ? 'gap-3 mb-4' : 'gap-3.5 mb-5'}`}>
      {/* Ihre aktuelle Wahl */}
      <div
        className={`mt-1.5 p-1 border border-gray-300 bg-[#C5C5C5] ${sp ? 'rounded-[14px]' : 'rounded-2xl'}`}
      >
        <span className={`block font-poppins text-white text-center pt-0.5 pb-1 ${sp ? 'text-xs pl-2' : 'text-[13px] pl-2.5'}`}>
          {t("current_choice_label")}
        </span>

        <div
          id="regular-service-card"
          className={`bg-white upsell-v2-card-border ${sp ? 'rounded-[10px] pt-[14px] px-3 pb-3' : 'rounded-xl pt-4 px-[14px] pb-[14px]'}`}
        >
          <div className="flex justify-between items-start mb-2">
            <div className="flex flex-col gap-0.5 min-w-0">
              <p className={`font-poppins text-black leading-tight m-0 ${sp ? 'text-[12px]' : 'text-[13px]'}`}>
                {t("regular_title")}<br/>{t("regular_title_line2")}
              </p>
              <p className={`font-poppins font-semibold text-black m-0 ${sp ? 'text-[12px]' : 'text-[13px]'}`}>
                {t("regular_price_label")}
              </p>
            </div>
            <div className="flex flex-col gap-0.5 items-end shrink-0">
              <p className={`font-poppins font-semibold text-[#9D9D9D] m-0 ${sp ? 'text-[23px]' : 'text-[25px]'}`}>
                ~{regularPrice.toFixed(2).replace('.', ',')} €
              </p>
              <p className={`font-poppins text-black m-0 ${sp ? 'text-[10px]' : 'text-[11px]'}`}>
                {t("regular_subtitle")}
              </p>
            </div>
          </div>

          <div
            className={`flex flex-col gap-1 pt-3 bg-[#F9F9F9] ${sp ? 'rounded-b-[8.5px] -mx-3 -mb-3 px-3 pb-[11px]' : 'rounded-b-[10.5px] -mx-3.5 -mb-3.5 px-3.5 pb-[13px]'}`}
          >
            <div className="flex items-center">
              <BulletDot />
              <span className={`font-poppins text-black ${sp ? 'text-xs' : 'text-[13px]'}`}>{t("regular_bullet_pharmacy")}</span>
            </div>
            <div className="flex items-center">
              <BulletDot />
              <span className={`font-poppins text-black ${sp ? 'text-xs' : 'text-[13px]'}`}>{t("regular_bullet_stock")}</span>
            </div>
            <div className="flex items-center">
              <BulletDot />
              <span className={`font-poppins text-black ${sp ? 'text-xs' : 'text-[13px]'}`}>{t("regular_bullet_price")}</span>
            </div>
          </div>
        </div>
      </div>

      {isPremiumCheaper && savingsAmount > 0 && (
        <div
          className={`mx-auto text-center flex items-center justify-center bg-white relative z-[2] w-fit rounded-[14px] shadow-[0px_0px_8px_0px_rgba(0,0,0,0.12)] ${sp ? 'px-3.5 py-2 -mt-[18px] -mb-4' : 'px-4 py-2.5 -mt-5 -mb-[18px]'}`}
        >
          <p className={`font-poppins text-black m-0 ${sp ? 'text-xs' : 'text-[13px]'}`}>
            <span className="font-medium">{t("desktop_savings_badge_prefix")}{' '}</span>
            <span
              className="upsell-v2-title-gradient"
            >
              ca.{savingsAmount.toFixed(0)} €
            </span>
            <span className="font-medium">{' '}{t("desktop_savings_badge_suffix")}</span>
          </p>
        </div>
      )}

      {/* Unsere Empfehlung */}
      <div
        className={`mt-1.5 p-1.5 border border-[#15967C] upsell-v2-recommendation-gradient ${sp ? 'rounded-[14px]' : 'rounded-2xl'}`}
      >
        <span className={`block font-poppins text-white text-center pt-0.5 pb-1 ${sp ? 'text-xs pl-2' : 'text-[13px] pl-2.5'}`}>
          {t("recommendation_label")}
        </span>

        <div
          id="premium-service-card"
          className={`bg-white ${sp ? 'rounded-[10px] pt-[14px] px-3 pb-3' : 'rounded-xl pt-4 px-[14px] pb-[14px]'}`}
        >
          <div className="flex justify-between items-start mb-2.5">
            <div>
              <p className={`font-poppins font-semibold text-black ${sp ? 'text-[12px]' : 'text-[13px]'}`}>
                {t("premium_price_label")}
              </p>
            </div>
            <div className="text-right shrink-0">
              <p
                className={`font-poppins font-semibold upsell-v2-price-gradient ${sp ? 'text-[21px]' : 'text-[25px]'}`}
              >
                {premiumPrice.toFixed(2).replace('.', ',')} €
              </p>
              <p className={`font-poppins font-medium text-black ${sp ? 'text-[10px]' : 'text-[11px]'}`}>
                {t("premium_subtitle")}
              </p>
            </div>
          </div>

          <div
            className={`flex flex-col gap-1 pt-3 bg-[#F9F9F9] ${sp ? 'rounded-b-[8.5px] -mx-3 -mb-3 px-3 pb-[11px]' : 'rounded-b-[10.5px] -mx-3.5 -mb-3.5 px-3.5 pb-[13px]'}`}
          >
            <div className="flex items-start gap-1.5">
              <CheckIconGreen />
              <span className={`font-poppins text-black ${sp ? 'text-[11px]' : 'text-[13px]'}`}>
                {t("premium_bullet_delivery")}
              </span>
            </div>
            <div className="flex items-start gap-1.5">
              <CheckIconGreen />
              <span className={`font-poppins text-black ${sp ? 'text-[11px]' : 'text-[13px]'}`}>
                {t("premium_bullet_stock")}
              </span>
            </div>
            <div className="flex items-start gap-1.5">
              <CheckIconGreen />
              <span className={`font-poppins text-black ${sp ? 'text-[11px]' : 'text-[13px]'}`}>
                {t("premium_bullet_support")}
              </span>
            </div>
            <div className="flex items-start gap-1.5">
              <CheckIconGreen />
              <span className={`font-poppins text-black ${sp ? 'text-[11px]' : 'text-[13px]'}`}>
                {t("premium_bullet_cannabis_id")}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
